import { useState } from "react";
import moment from "moment";

import McapFilters from "./components/McapFilters";
import { dateTimeFormat } from "../../utils/dateFormatUtils";

const initialRangeValue = [{ startDate: undefined, endDate: undefined, key: "selection" }];

/**
 * The date-range + School/Section filter state every MCAP screen reports
 * against, and the shared filter bar that edits it.
 *
 * Was state lifted once in the old hub page (Mcap.js) and threaded into
 * every tab as props, because all eight tabs lived under one route and
 * shared one instance. Now each is its own top-level, sidebar-navigable
 * route/component — see routes/index.js — so each calls this hook itself.
 * The tradeoff that comes with that: navigating from Overview to
 * Implementation no longer carries the school/date picked on Overview along
 * with it, since there is no longer one shared page for it to live on. A
 * teacher/admin picking a school back up on each screen is the honest cost
 * of "these are different pages" rather than "these are tabs of one page",
 * not a bug to route around with cross-page state this migration wasn't
 * asked to add.
 */
export default function useMcapFilters() {
  const [range, setRange] = useState(initialRangeValue);
  const [hasUserSelectedDate, setHasUserSelectedDate] = useState(false);
  const [schoolId, setSchoolId] = useState("");
  const [sectionId, setSectionId] = useState("");

  const handleRangeChange = ({ selection }) => {
    const { startDate, endDate } = selection;
    const safeEndDate = endDate instanceof Date && !isNaN(endDate) ? endDate : startDate;
    setRange([{ ...selection, endDate: safeEndDate }]);
    setHasUserSelectedDate(true);
  };

  const handleRangeClear = (setIsOpen) => {
    if (setIsOpen) setIsOpen(false);
    setHasUserSelectedDate(false);
    setRange(initialRangeValue);
  };

  const filters = {
    ...(hasUserSelectedDate && range[0]?.startDate
      ? { startDate: moment(range[0].startDate).startOf("day").format(dateTimeFormat.apiDateTimeFormat) }
      : {}),
    ...(hasUserSelectedDate && range[0]?.endDate
      ? { endDate: moment(range[0].endDate).endOf("day").format(dateTimeFormat.apiDateTimeFormat) }
      : {}),
    ...(schoolId ? { schoolId } : {}),
    ...(sectionId ? { sectionId } : {}),
  };

  // A function, not a pre-built element: each screen exports a different
  // metric, so each calls this with its own exportMetric rather than
  // sharing one fixed filter bar instance. `showSchoolPicker: false` is for
  // Overview specifically — a ranked school picker that scopes the whole
  // platform-wide summary down to one school out of what could be thousands
  // is a drill-down, and Overview's entire purpose is staying above that
  // layer. The School/Section picker still belongs everywhere else (it's
  // what makes Implementation's own school-by-school panels navigable).
  const renderFilterBar = (exportMetric, options = {}) => (
    <McapFilters
      range={range}
      onRangeChange={handleRangeChange}
      onRangeClear={handleRangeClear}
      hasUserSelectedDate={hasUserSelectedDate}
      schoolId={schoolId}
      sectionId={sectionId}
      onSchoolChange={setSchoolId}
      onSectionChange={setSectionId}
      exportMetric={exportMetric}
      exportParams={filters}
      showSchoolPicker={options.showSchoolPicker !== false}
    />
  );

  return { filters, renderFilterBar };
}
